import { endpoint } from '@library/endpoint'
import { getDomain, getEnvironment, isDev } from '@library/environment/isomorphic'

import routes from 'config/routes'
import { fetch, fetchRaw } from 'utils/fetch'
import { UserOrder } from 'utils/types/order'

export function applyPromo(accessToken: string, promoCode: string) {
  return fetch(
    accessToken,
    `${endpoint('core')}/user/current/applyPromotionCode/${promoCode}`,
    {},
    'POST',
  )
}

export function fetchUserCredit(accessToken: string) {
  return fetch<{
    data: {
      balance: string
    }
  }>(accessToken, `${endpoint('core')}/user/current/balance`, {}, 'GET')
}

export function fetchReferralOffer(accessToken: string) {
  return fetch<{
    data: {
      code: string
      creditFormatted: string
      description: string
      details: string[]
      discountDuration: {
        value: number
        unit: string
        formatted: string
      }
      expiry: null
      firstBoxDiscountFormatted: string
      firstMonthDiscountFormatted: string
      originalCampaignDDetails: null
      title: string
      secondBoxDiscountFormatted: string
    }
  }>(accessToken, `${endpoint('core')}/user/current/raf-campaign-details`, {}, 'GET')
}

export function fetchPromo(accessToken: string) {
  return fetch(accessToken, `${endpoint('core')}${routes.core.userPromo}`, {}, 'GET')
}

export function fromAuthUserId(authUserId: string) {
  const domain = isDev() ? 'gousto.info' : getDomain()
  const environmentName = isDev() ? 'staging' : getEnvironment()

  return fetch<{ data: { userId: number } }>(
    null,
    // manually constructing URL to avoid normal logic to use public URLs when running locally
    `http://${environmentName}-core.${domain}/internal/user/fromauthid/${authUserId}`,
    {},
    'GET',
  )
}

export function fetchUser(accessToken: string) {
  return fetch<{ data: { user: { id: string } } }>(
    accessToken,
    `${endpoint('core')}${routes.core.currentUser}`,
    {},
    'GET',
  )
}

export type ShippingAddress = {
  billingDefault: boolean
  companyname: string
  country: string
  deleted: boolean
  deliveryInstructions: string
  id: string
  line1: string
  line2: string
  line3: string
  name: string
  postcode: string
  premiumDelivery: boolean
  shippingDefault: boolean
  state: string
  town: string
  userId: number
}

export function fetchShippingAddresses(accessToken: string) {
  return fetch<{
    data: ShippingAddress[]
  }>(accessToken, `${endpoint('core')}${routes.core.userAddress}`, { type: 'shipping' }, 'GET')
}

export function fetchUserOrders(accessToken: string, reqData?: Record<string, unknown>) {
  return fetch<{
    data: UserOrder[]
  }>(accessToken, `${endpoint('core')}${routes.core.userOrders}`, reqData, 'GET')
}

export function reactivate(accessToken: string, reqData: { userId: string }) {
  return fetch(accessToken, `${endpoint('core')}/user/${reqData.userId}/restore`, {}, 'PUT')
}

export function verifyAge(accessToken: string, userId: string) {
  return fetch(accessToken, `${endpoint('core')}/user/${userId}`, { age_verified: 1 }, 'PUT')
}

export function checkDuplicateUser(reqData?: Record<string, unknown>) {
  return fetch(null, `${endpoint('core')}/user/check-duplicate`, reqData, 'POST')
}

export function referralDetails(accessToken: string, reqData?: Record<string, unknown>) {
  return fetch<{
    data: {
      referralCount: number
      referralCredit: number
    }
  }>(accessToken, `${endpoint('core')}/user/current/referralDetails`, reqData, 'GET')
}

export function addPaymentMethod(
  accessToken: string,
  reqData: Record<string, unknown>,
  userId: string,
) {
  return fetch(accessToken, `${endpoint('core')}/user/${userId}/paymentMethod`, reqData, 'PUT')
}

export function fetchUserAddresses(accessToken: string) {
  return fetch(accessToken, `${endpoint('core')}${routes.core.userAddress}`, {}, 'GET')
}
export function deleteMarketingSubscription(
  authUserId: string,
  marketingType: string,
  marketingUnsubscribeToken: string,
) {
  return fetchRaw(
    `${endpoint('core')}/user/${authUserId}/marketing/${marketingType}`,
    { marketing_unsubscribe_token: marketingUnsubscribeToken },
    { method: 'PUT', useOverwriteRequestMethod: false },
  )
}
